import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Ghost, Wind, Weight, Droplets, Skull, Coins } from 'lucide-react';
import { fadeInUp } from '../../lib/animations';
import DecimalInput from '../ui/DecimalInput';


const HABITOS = [
  { id: 'pneu', title: 'Andar com Pneu Murcho', icon: Droplets, extraConsumption: 0.05, desc: 'Aumenta atrito, consume até 5% a mais.' },
  { id: 'ar', title: 'Ar condicionado 100%', icon: Wind, extraConsumption: 0.10, desc: 'Força o compressor, gasta 10% a mais.' },
  { id: 'peso', title: 'Porta-malas cheio', icon: Weight, extraConsumption: 0.03, desc: 'A cada 40kg extras, gasta 3% a mais.' },
  { id: 'pe_pesado', title: 'Acelerações Bruscas', icon: Ghost, extraConsumption: 0.15, desc: 'Pé afundado gasta até 15% a mais.' },
  { id: 'ponto_morto', title: 'Banguela (Ponto Morto)', icon: Skull, extraConsumption: 0.02, desc: 'A injeção corta no freio motor, mas não na banguela.' }
];

const SimuladorPrejuizos: React.FC = () => {
  const [kmMensal, setKmMensal] = useState(1200);
  const [consumoIdeal, setConsumoIdeal] = useState(12);
  const [precoCombustivel, setPrecoCombustivel] = useState(5.50);
  const [habitosSelecionados, setHabitosSelecionados] = useState<string[]>([]);

  const litrosIdeais = kmMensal / consumoIdeal;
  const gastoIdeal = litrosIdeais * precoCombustivel;

  const multiplicadorExtra = habitosSelecionados.reduce((acc, habId) => {
    const hab = HABITOS.find(h => h.id === habId);
    return acc + (hab?.extraConsumption || 0);
  }, 0);

  const gastoComMausHabitos = gastoIdeal * (1 + multiplicadorExtra);
  const prejuizoMes = gastoComMausHabitos - gastoIdeal;
  const prejuizoAno = prejuizoMes * 12;

  const toggleHabito = (id: string) => {
    setHabitosSelecionados(prev =>
      prev.includes(id) ? prev.filter(h => h !== id) : [...prev, id]
    );
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <motion.div variants={fadeInUp} className="overflow-hidden rounded-2xl bg-slate-900 shadow-xl shadow-slate-900/40 border border-slate-800">
        <div className="bg-amber-400 p-6 sm:p-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-950/30">
            <Coins className="h-7 w-7 text-white" strokeWidth={1.8} />
          </div>
          <h2 className="font-display text-2xl font-bold text-white mb-2">Simulador de Maus Hábitos</h2>
          <p className="text-slate-300 text-sm max-w-lg mx-auto">
            Descubra quanto dinheiro você joga pela janela ao descuidar da calibragem e da forma de dirigir.
          </p>
        </div>

        <div className="p-6 sm:p-8 grid gap-8 md:grid-cols-2">
          {/* Dados Iniciais */}
          <div className="space-y-4">
            <h3 className="font-semibold text-white border-b border-slate-800 pb-2">Seu Perfil Base</h3>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">KM Rodados por Mês</label>
              <DecimalInput value={kmMensal} onChange={setKmMensal} className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">Consumo Original (km/l)</label>
              <DecimalInput value={consumoIdeal} onChange={setConsumoIdeal} className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">Preço Combustível (R$)</label>
              <DecimalInput value={precoCombustivel} onChange={setPrecoCombustivel} className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white focus:border-amber-400 focus:outline-none" />
            </div>
          </div>

          {/* Hábitos */}
          <div className="space-y-4">
            <h3 className="font-semibold text-white border-b border-slate-800 pb-2 text-red-400">Confesse Culpas</h3>
            <div className="space-y-2">
              {HABITOS.map(habito => {
                const Icon = habito.icon;
                const active = habitosSelecionados.includes(habito.id);
                return (
                  <label key={habito.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${active ? 'bg-red-400/10 border-red-400/30' : 'bg-slate-900 border-slate-700 hover:bg-slate-800'}`}>
                    <input type="checkbox" checked={active} onChange={() => toggleHabito(habito.id)} className="w-4 h-4 text-red-400 rounded border-slate-300" />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-white flex items-center gap-1.5">
                        <Icon className="h-4 w-4 text-slate-400" /> {habito.title} (+{habito.extraConsumption * 100}%)
                      </div>
                      <div className="text-xs text-slate-400">{habito.desc}</div>
                    </div>
                  </label>
                )
              })}
            </div>
          </div>
        </div>

        {/* Resultado Final */}
        <div className="border-t border-slate-800 bg-slate-800 p-6 sm:p-8">
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-slate-900 rounded-xl p-5 border border-slate-700 text-center shadow-sm">
              <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1">Evasão Mensal</p>
              <div className="font-display text-2xl font-bold text-red-400">
                + R$ {prejuizoMes.toFixed(2)}
              </div>
            </div>
            <div className="bg-red-400/10 rounded-xl p-5 border border-red-400/30 text-center shadow-sm shadow-red-900/20">
              <p className="text-xs text-red-400 font-bold uppercase tracking-wider mb-1">Prejuízo Anual</p>
              <div className="font-display text-3xl font-black text-red-400">
                R$ {prejuizoAno.toFixed(2)}
              </div>
            </div>
          </div>
          <p className="text-center text-xs text-slate-400 mt-4">
            Isso significa jogar fora <strong>{(multiplicadorExtra * 100).toFixed(0)}%</strong> do seu tanque por conta de costumes ruins.
          </p>
        </div>
      </motion.div>

    </div>
  );
};

export default SimuladorPrejuizos;




